"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { forgotPassword } from "@/lib/api"
import { ArrowLeft } from "lucide-react"
import { authPageShellClassName } from "@/lib/mobile-bottom-nav"

const forgotPasswordSchema = z.object({
  email: z.string().email("Email nije validan"),
})

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>

export default function ZaboravljenaLozinkaPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const router = useRouter()

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  })

  const onSubmit = async (data: ForgotPasswordFormValues) => {
    setIsLoading(true)
    setError(null)
    setSuccess(false)
    try {
      await forgotPassword(data.email)
      setSuccess(true)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Greška pri slanju emaila"
      setError(errorMessage)
      console.error("Forgot password error:", error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={authPageShellClassName}>
      <div className="w-full max-w-md">
        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">
              Zaboravljena lozinka
            </CardTitle>
            <CardDescription className="text-center">
              Unesite vaš email adresu i poslaćemo vam link za resetovanje
              lozinke
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="space-y-4">
                <div className="p-4 text-sm text-[#065F46] dark:text-[#D1FAE5] bg-[#D1FAE5] dark:bg-[#D1FAE5]/30 border border-[#D1FAE5] dark:border-[#D1FAE5]/40 rounded-md">
                  <p className="font-medium mb-1">Email je poslat!</p>
                  <p>
                    Ako postoji nalog sa ovom email adresom, poslali smo vam
                    link za resetovanje lozinke. Proverite vašu email poštu.
                  </p>
                </div>
                <Button
                  onClick={() => router.push("/prijava")}
                  className="w-full"
                  variant="outline"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Nazad na prijavu
                </Button>
              </div>
            ) : (
              <>
                {error && (
                  <div className="mb-4 p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
                    {error}
                  </div>
                )}
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-4"
                  >
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="vas@email.com"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isLoading}
                    >
                      {isLoading ? "Slanje..." : "Pošalji link za reset"}
                    </Button>
                  </form>
                </Form>
                <div className="mt-4 text-center">
                  <Link
                    href="/prijava"
                    className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Nazad na prijavu
                  </Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

